import { useMemo, memo } from 'react';
import {
  Table,
  TypographyWithTooltip,
  useTranslations,
  IUseTableReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  IMaterialTableOptions,
} from '@uniqkey-frontend/shared-app';
import {
  GroupUsersResponseModel,
} from '@uniqkey-backend-partner/api-client';
import { TGetGroupUsersMethod } from '../../../hooks/useGroupsAPI/interfaces';

export interface IGroupUsersTableRow extends GroupUsersResponseModel {}

interface IGroupUsersTableProps extends
  Omit<IUseTableReturn<IGroupUsersTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Omit<
    IUseQueryTableFetchReturn<TGetGroupUsersMethod>,
    'data' | 'resetQuery' | 'isFetchedAfterMount'
  >
{
  groupUsers: IUseQueryTableFetchReturn<TGetGroupUsersMethod>['data'];
  selectedGroupUsers: IUseTableReturn<IGroupUsersTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  options?: IMaterialTableOptions<IGroupUsersTableRow>;
}

const GroupUsersTable = (props: IGroupUsersTableProps) => {
  const {
    groupUsers,
    isLoading,
    total,
    localization,
    activePage,
    setActivePage,
    perPage,
    onPerPageChange,
    columnOrderBy,
    onColumnOrderByChange,
    columnOrderDirection,
    onColumnOrderDirectionChange,
    selectedGroupUsers,
    onRowSelectionChange,
    createColumns,
    options,
  } = props;

  const { t } = useTranslations();

  const columns = useMemo(() => createColumns([
    {
      title: t('groupPage.name'),
      field: 'name',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.name}
        </TypographyWithTooltip>
      ),
      width: '50%',
    },
    {
      title: t('groupPage.email'),
      field: 'email',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.email}
        </TypographyWithTooltip>
      ),
      width: '50%',
      sorting: false,
    },
  ]), [createColumns, t]);

  return (
    <Table
      t={t}
      totalItems={total}
      columns={columns}
      data={groupUsers}
      isLoading={isLoading}
      options={options}
      activePage={activePage}
      onActivePageChange={setActivePage}
      perPage={perPage}
      onPerPageChange={onPerPageChange}
      columnOrderBy={columnOrderBy}
      onColumnOrderByChange={onColumnOrderByChange}
      columnOrderDirection={columnOrderDirection}
      onColumnOrderDirectionChange={onColumnOrderDirectionChange}
      selectedRows={selectedGroupUsers}
      onSelectionChange={onRowSelectionChange}
      localization={localization}
      rowSelectByKey="partnerUserId"
      rowHeight={51}
    />
  );
};

export default memo(GroupUsersTable);
